import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Section, Button, Input, Select } from "@/components/ui/kit";
import { Save, Info, ChevronDown } from "lucide-react";

export default function DonationAdd() {
  const nav = useNavigate();
  const [purposes, setPurposes] = useState([]);
  const [donors, setDonors] = useState([]);
  const [years, setYears] = useState([]);
  
  const [paymentModes, setPaymentModes] = useState([]);
  const [amountTypes, setAmountTypes] = useState([]);
  
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0,10);
  
  const [f, setF] = useState({
    donorId: "", donationDate: today, donatedAmount: "", paymentModeId: "",
    sectorId: "", projectId: "", activityId: "", donationType: "SELF",
    matchedByDonorId: "", matchedAmount: "", academicYearId: "",
    amountTypeId: "", transactionReferenceNumber: "" 
  });

  useEffect(() => {
    api.get("/donation-purposes").then(r => setPurposes(r.data.data));
    api.get("/donors", { params: { size: 200 } }).then(r => setDonors(r.data.data.content));
    api.get("/master/academic-years").then(r => setYears(r.data.data));
    
    api.get("/master/payment-modes").then(r => setPaymentModes(r.data.data || r.data));
    api.get("/master/amount-types").then(r => setAmountTypes(r.data.data || r.data));
  }, []);

  const sectors = useMemo(() => purposes.filter(p => p.type === "SECTOR"), [purposes]);
  const projects = useMemo(() => purposes.filter(p => p.type === "PROJECT" && (!f.sectorId || String(p.parentId) === String(f.sectorId))), [purposes, f.sectorId]);
  const activities = useMemo(() => purposes.filter(p => p.type === "ACTIVITY" && (!f.projectId || String(p.parentId) === String(f.projectId))), [purposes, f.projectId]);

  // Format donors for the SearchableSelect component (mapping id & name)
  const donorOptions = useMemo(() => {
    return donors.map(d => ({
      id: d.donorId,
      name: `${d.donorCode || ''} • ${d.salutation ? `${d.salutation} ` : ""}${d.name}`
    }));
  }, [donors]);

  const corporateDonorOptions = useMemo(() => {
    return donors
      .filter(d => d.donorType === "CORPORATE")
      .map(d => ({
        id: d.donorId,
        name: d.name
      }));
  }, [donors]);

  // Format purposes for searchable selects
  const sectorOptions = useMemo(() => sectors.map(s => ({ id: s.id, name: s.name })), [sectors]);
  const projectOptions = useMemo(() => projects.map(p => ({ id: p.id, name: p.name })), [projects]);
  const activityOptions = useMemo(() => activities.map(a => ({ id: a.id, name: a.name })), [activities]);

  const selectedCurrencyCode = useMemo(() => {
    if (!f.amountTypeId) return null;
    return amountTypes.find(a => String(a.amount_type_id) === String(f.amountTypeId))?.code;
  }, [f.amountTypeId, amountTypes]);

  useEffect(() => {
    const current = years.find(y => y.isCurrent);
    if (current && !f.academicYearId) setF(v => ({ ...v, academicYearId: current.id }));
    // eslint-disable-next-line
  }, [years]);

  const upd = (k) => (e) => setF(v => ({ ...v, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const purposeId = f.activityId || f.projectId || f.sectorId;
    if (!f.donorId) return toast.error("Please select a donor");
    if (!purposeId) return toast.error("Please select a sector / project / activity");
    if (!f.donatedAmount) return toast.error("Please enter an amount");
    if (!f.amountTypeId) return toast.error("Please select an amount type");
    if (!f.paymentModeId) return toast.error("Please select a payment mode");

    setBusy(true);
    try {
      const payload = {
        donorId: Number(f.donorId),
        donationDate: f.donationDate,
        amount: Number(f.donatedAmount), 
        currency_code: selectedCurrencyCode, 
        paymentModeId: Number(f.paymentModeId),
        amountTypeId: Number(f.amountTypeId),
        transactionReferenceNumber: f.transactionReferenceNumber,
        purposeId: Number(purposeId), 
        donationType: f.donationType,
        matchedByDonorId: f.donationType === "MATCHED" && f.matchedByDonorId ? Number(f.matchedByDonorId) : null,
        matchedAmount: f.donationType === "MATCHED" && f.matchedAmount ? Number(f.matchedAmount) : null,
        academicYearId: f.academicYearId ? Number(f.academicYearId) : null,
      };
      
      const r = await api.post("/donations", payload);
      toast.success(`Donation recorded • Receipt ${r.data?.data?.receiptNumber || 'Saved'}`);
      nav("/admin/donations");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to record donation");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="donation-add-form">
      <Section title="Add Donation" kicker="Contributions" right={
        <Button type="submit" disabled={busy} data-testid="save-donation-btn"><Save className="w-4 h-4"/> Save donation</Button>
      }/>

      <div className="rounded-xl border border-emerald-900/10 bg-white p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
        
        {/* Searchable Donor Select */}
        <div className="flex flex-col gap-1" data-testid="fld-donor">
          <label className="text-xs font-medium text-emerald-950">Donor</label>
          <SearchableSelect
            placeholder="— select donor —"
            options={donorOptions}
            value={f.donorId}
            onChange={val => setF(v => ({ ...v, donorId: val }))}
          />
        </div>
        
        <Input label="Donation date" type="date" testid="fld-date" value={f.donationDate} onChange={upd("donationDate")} />

        <div className="flex flex-col gap-1">
          <div className="flex gap-2 items-end">
            <div className="w-32 shrink-0">
              <Select label="Currency" testid="fld-amount-type" value={f.amountTypeId} onChange={upd("amountTypeId")}>
                <option value="">— select —</option>
                {amountTypes.map(a => (
                  <option key={a.amount_type_id} value={a.amount_type_id}>
                    {a.code}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex-1">
              <Input label="Amount" type="number" min="1" step="0.01" testid="fld-amount" value={f.donatedAmount} onChange={upd("donatedAmount")} />
            </div>
          </div>
          {selectedCurrencyCode && selectedCurrencyCode !== 'INR' && (
            <div className="flex items-center gap-1 text-[11px] text-amber-700 mt-1 bg-amber-50 p-1.5 rounded-md">
              <Info className="w-3 h-3" />
              INR equivalent will be calculated automatically based on the exchange rate for {f.donationDate}.
            </div>
          )}
        </div>

        <Select label="Payment mode" testid="fld-mode" value={f.paymentModeId} onChange={upd("paymentModeId")}>
          <option value="">— select mode —</option>
          {paymentModes.map(p => (
            <option key={p.payment_mode_id} value={p.payment_mode_id}>
              {p.description}
            </option>
          ))}
        </Select>

        <Input label="Transaction Ref Number" type="text" placeholder="Txn ID, Cheque No, UTR No etc." testid="fld-txn-ref" value={f.transactionReferenceNumber} onChange={upd("transactionReferenceNumber")} />

        {/* Searchable Sector, Project, Activity */}
        <div className="flex flex-col gap-1" data-testid="fld-sector">
          <label className="text-xs font-medium text-emerald-950">Sector</label>
          <SearchableSelect
            placeholder="— select —"
            options={sectorOptions}
            value={f.sectorId}
            onChange={val => setF(v => ({ ...v, sectorId: val, projectId: "", activityId: "" }))}
          />
        </div>

        <div className="flex flex-col gap-1" data-testid="fld-project">
          <label className="text-xs font-medium text-emerald-950">Project</label>
          <SearchableSelect
            placeholder="— select —"
            options={projectOptions}
            value={f.projectId}
            disabled={!f.sectorId && projectOptions.length === 0}
            onChange={val => setF(v => ({ ...v, projectId: val, activityId: "" }))}
          />
        </div>

        <div className="flex flex-col gap-1" data-testid="fld-activity">
          <label className="text-xs font-medium text-emerald-950">Activity</label>
          <SearchableSelect
            placeholder="— select —"
            options={activityOptions}
            value={f.activityId}
            disabled={!f.projectId && activityOptions.length === 0}
            onChange={val => setF(v => ({ ...v, activityId: val }))}
          />
        </div>

        <Select label="Donation type" testid="fld-type" value={f.donationType} onChange={upd("donationType")}>
          <option value="SELF">Self</option>
          <option value="MATCHED">Matched by employer</option>
        </Select>
        <Select label="Academic year" testid="fld-ay" value={f.academicYearId} onChange={upd("academicYearId")}>
          <option value="">— none —</option>
          {years.map(y => <option key={y.id} value={y.id}>{y.code}{y.isCurrent ? " (current)" : ""}</option>)}
        </Select>

        {f.donationType === "MATCHED" && (
          <>
            <div className="flex flex-col gap-1" data-testid="fld-matched-donor">
              <label className="text-xs font-medium text-emerald-950">Matched by (donor)</label>
              <SearchableSelect
                placeholder="— select employer/donor —"
                options={corporateDonorOptions}
                value={f.matchedByDonorId}
                onChange={val => setF(v => ({ ...v, matchedByDonorId: val }))}
              />
            </div>
            <Input label="Matched amount" type="number" testid="fld-matched-amount" value={f.matchedAmount} onChange={upd("matchedAmount")} />
          </>
        )}
      </div>
    </form>
  );
}

// --- Custom Searchable Select Component ---
function SearchableSelect({ value, onChange, options, placeholder, disabled }) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (value) {
      const selectedOpt = options.find(o => String(o.id) === String(value));
      if (selectedOpt) setSearch(selectedOpt.name);
    } else {
      setSearch("");
    }
  }, [value, options]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        const selectedOpt = options.find(o => String(o.id) === String(value));
        setSearch(selectedOpt ? selectedOpt.name : "");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value, options]);

  const filtered = options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="relative" ref={wrapperRef}>
      <Input
        placeholder={placeholder}
        value={search}
        disabled={disabled}
        onChange={e => {
          setSearch(e.target.value);
          setIsOpen(true);
          if (value) onChange(""); 
        }}
        onClick={() => { if (!disabled) setIsOpen(true); }}
      />
      <ChevronDown className={`absolute right-3 top-3 w-4 h-4 pointer-events-none transition-transform ${isOpen ? "rotate-180" : ""} ${disabled ? "text-emerald-900/20" : "text-emerald-900/40"}`} />

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto bg-white border border-emerald-900/10 rounded-lg shadow-xl">
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-center text-muted-foreground">No matches found</div>
          ) : (
            <>
              {value && (
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-b border-emerald-50 transition-colors"
                  onClick={() => { onChange(""); setSearch(""); setIsOpen(false); }}
                >
                  Clear Selection
                </button>
              )}
              {filtered.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-emerald-50 transition-colors ${String(value) === String(opt.id) ? "bg-emerald-50/50 font-semibold text-emerald-900" : "text-emerald-950"}`}
                  onClick={() => {
                    onChange(opt.id);
                    setSearch(opt.name);
                    setIsOpen(false);
                  }}
                >
                  {opt.name}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}